import Button from '@jetbrains/ring-ui/components/button/button'
import Link from '@jetbrains/ring-ui/components/link/link'
import React from 'react'

import {useDisplayErrorAlert} from '../../../HttpsConfigurationPage.hooks'

import styles from './UploadButton.css'

const validate = async (file: File, sizeLimit: number) => {
  if (file.size > sizeLimit) {
    return new Error(
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      `Upload failed: The maximum supported file size is ${Math.round(sizeLimit / 1024)} KB.`,
    )
  }

  try {
    await file.arrayBuffer()
  } catch {
    return new Error(
      'Upload failed: Unable to read the file. Check your system settings and ensure ' +
        'your browser has permissions to access the target file.',
    )
  }

  return null
}

type Props = {
  children?: React.ReactNode
  uploadDescription?: string
  accept?: string
  onFileUpload?: Function
  fileSizeLimit?: number
}
const DEFAULT_FILE_SIZE_LIMIT = 16384
function UploadButton({
  children,
  uploadDescription,
  accept,
  onFileUpload,
  fileSizeLimit = DEFAULT_FILE_SIZE_LIMIT,
}: Props) {
  const [blob, setBlob] = React.useState<File>()
  const [validationError, setValidationError] = React.useState<Error | null>(null)

  const dragEnterCounter = React.useRef(0)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  useDisplayErrorAlert(validationError, 10000)

  const clickHandler = React.useCallback(() => {
    fileInputRef.current?.click()
  }, [])
  const uploadHandler = React.useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.currentTarget.files?.[0]
      e.currentTarget.value = ''
      if (file == null) {
        return
      }

      const error = await validate(file, fileSizeLimit)
      if (error != null) {
        setValidationError(error)
        return
      }

      setBlob(file)
      onFileUpload?.(file)
    },
    [onFileUpload, fileSizeLimit],
  )

  const dragEnterHandler = React.useCallback((e: React.DragEvent) => {
    e.currentTarget.classList.add(styles.dragover)
    dragEnterCounter.current++
  }, [])
  const dragLeaveHandler = React.useCallback((e: React.DragEvent) => {
    dragEnterCounter.current--

    if (dragEnterCounter.current === 0) {
      e.currentTarget.classList.remove(styles.dragover)
    }
  }, [])
  const dropHandler = React.useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault()
      dragEnterCounter.current = 0
      e.currentTarget.classList.remove(styles.dragover)

      const file = e.dataTransfer.files[0]
      const error = await validate(file, fileSizeLimit)
      if (error != null) {
        setValidationError(error)
        return
      }

      setBlob(file)
      onFileUpload?.(file)
    },
    [onFileUpload, fileSizeLimit],
  )
  const dragOverHandler = React.useCallback((e: React.DragEvent) => {
    e.preventDefault()
  }, [])

  return (
    <div
      className={styles.wrapper}
      onDragEnter={dragEnterHandler}
      onDragLeaveCapture={dragLeaveHandler}
      onDragOver={dragOverHandler}
      onDrop={dropHandler}
    >
      {blob == null ? (
        <>
          <Button onClick={clickHandler}>{children}</Button>
          <span className={styles.caption}>{'or drag and drop it here'}</span>
        </>
      ) : (
        <>
          <span>{`${uploadDescription ?? ''}${blob.name}`}</span>
          <Link className={styles.caption} onClick={clickHandler} pseudo>
            {'Change...'}
          </Link>
        </>
      )}
      <input
        className={styles.input}
        ref={fileInputRef}
        type={'file'}
        accept={accept}
        onChange={uploadHandler}
      />
    </div>
  )
}

export default React.memo<Props>(UploadButton)
